import { TextField, Typography, IconButton } from '@mui/material'
import { useState } from 'react'
import { useMutation } from '@cedarjs/web'
import { gql } from '@apollo/client'
import { toast } from 'react-hot-toast'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { FormTemplate } from 'types/graphql'

const UPDATE_FORM_TEMPLATE_TITLE_MUTATION = gql`
  mutation UpdateFormTemplateTitle(
    $id: String!
    $input: UpdateFormTemplateInput!
  ) {
    updateFormTemplate(id: $id, input: $input) {
      id
      title
    }
  }
`

interface TitleSettingsProps {
  formTemplate: FormTemplate
  onUpdate: () => void
}

const TitleSettings = ({ formTemplate, onUpdate }: TitleSettingsProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [title, setTitle] = useState(formTemplate.title || '')
  const [isSaving, setIsSaving] = useState(false)

  const [updateFormTemplate] = useMutation(UPDATE_FORM_TEMPLATE_TITLE_MUTATION)

  const handleStartEdit = () => {
    setTitle(formTemplate.title || '')
    setIsEditing(true)
  }

  const handleCancel = () => {
    setTitle(formTemplate.title || '')
    setIsEditing(false)
  }

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title cannot be empty')
      return
    }

    if (title.trim() === formTemplate.title) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      await updateFormTemplate({
        variables: {
          id: formTemplate.id,
          input: {
            title: title.trim(),
          },
        },
      })
      toast.success('Title updated successfully!')
      setIsEditing(false)
      onUpdate() // Trigger refetch of parent data
    } catch (error) {
      console.error('Error updating form template title:', error)
      toast.error('Failed to update title')
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <TextField
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyPress}
          variant="outlined"
          size="small"
          autoFocus
          disabled={isSaving}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '2.125rem',
              fontWeight: 400,
              lineHeight: 1.235,
            },
          }}
        />
        <IconButton
          onClick={handleSave}
          disabled={isSaving || !title.trim()}
          color="primary"
          size="small"
        >
          <CheckIcon />
        </IconButton>
        <IconButton onClick={handleCancel} disabled={isSaving} size="small">
          <CloseIcon />
        </IconButton>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <Typography variant="h4" component="h1">
        {formTemplate.title}
      </Typography>
      <IconButton
        onClick={handleStartEdit}
        size="small"
        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </div>
  )
}

export default TitleSettings
